import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/** @param {HTMLElement} component */
function namespaceModalInstance(component) {
  const sourceId = component.dataset.instanceId;
  if (!sourceId || sourceId.endsWith('-quick-add')) return;
  const instanceId = `${sourceId}-quick-add`;
  for (const element of [component, ...component.querySelectorAll('*')]) {
    for (const attribute of ['id', 'for', 'form', 'name', 'aria-labelledby', 'aria-describedby', 'data-instance-id', 'data-hydration-key']) {
      const value = element.getAttribute(attribute);
      if (value?.includes(sourceId)) element.setAttribute(attribute, value.replaceAll(sourceId, instanceId));
    }
  }
}

/**
 * Give detached Quick Add content stable identities before morph matches its nodes.
 * @param {Element} content
 */
export function prepareRingSizingForModal(content) {
  for (const component of content.querySelectorAll('ring-sizing-component')) {
    if (component instanceof HTMLElement) namespaceModalInstance(component);
  }
}

/**
 * @typedef {object} RingSizingRefs
 * @property {HTMLFieldSetElement} question
 * @property {HTMLElement} ringPanel
 * @property {HTMLElement} kitPanel
 * @property {HTMLElement} kitContent
 * @property {HTMLElement} status
 * @property {HTMLButtonElement} retryButton
 */

/** @extends {Component<RingSizingRefs>} */
class RingSizingComponent extends Component {
  requiredRefs = ['question', 'ringPanel', 'kitPanel', 'kitContent', 'status', 'retryButton'];

  /** @type {AbortController | undefined} */
  #request;
  #generation = 0;
  #kitReady = true;
  /** @type {Element | null} */
  #scope = null;
  /** @type {Map<HTMLElement, boolean>} */
  #suppressed = new Map();
  #observer = new MutationObserver(() => this.#syncPurchases());

  connectedCallback() {
    super.connectedCallback();
    this.#prepareModalIdentity();
    this.#scope = this.closest('dialog, product-card, .shopify-section');
    if (this.#scope) this.#observer.observe(this.#scope, { childList: true, subtree: true });
    this.refs.question.disabled = false;
    this.#syncMode();
    if (!this.#kitReady) void this.selectKitVariant();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#prepareModalIdentity();
    this.refs.question.disabled = false;
    this.#syncMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.#request?.abort();
    this.#observer.disconnect();
    this.#restorePurchases();
    this.#scope = null;
  }

  /** @param {Event} event */
  changeMode(event) {
    if (!(event.target instanceof HTMLInputElement) || !event.target.checked) return;
    this.#syncMode();
  }

  #syncMode() {
    const kitSelected = this.querySelector('input[value="kit"]:checked') !== null;
    this.refs.ringPanel.hidden = kitSelected;
    this.refs.ringPanel.inert = kitSelected;
    this.refs.kitPanel.hidden = !kitSelected;
    this.refs.kitPanel.inert = !kitSelected;
    this.#syncPurchases();
  }

  #prepareModalIdentity() {
    if (this.closest('quick-add-dialog')) namespaceModalInstance(this);
  }

  #syncPurchases() {
    if (!this.#scope || !this.isConnected) return;
    if (!this.querySelector('input[value="kit"]:checked')) {
      this.#restorePurchases();
      return;
    }

    for (const form of this.#scope.querySelectorAll('product-form-component')) {
      if (!(form instanceof HTMLElement) || this.contains(form) || form.dataset.productId !== this.dataset.productId) continue;
      const wrapper = form.closest('.buy-buttons-block') ?? form;
      if (wrapper instanceof HTMLElement) this.#suppress(wrapper);
    }
    for (const sticky of this.#scope.querySelectorAll('sticky-add-to-cart')) {
      if (sticky instanceof HTMLElement) this.#suppress(sticky);
    }
  }

  /** @param {HTMLElement} element */
  #suppress(element) {
    if (!this.#suppressed.has(element)) this.#suppressed.set(element, element.inert);
    element.classList.add('ring-sizing-purchase-hidden');
    element.inert = true;
  }

  #restorePurchases() {
    for (const [element, wasInert] of this.#suppressed) {
      element.classList.remove('ring-sizing-purchase-hidden');
      element.inert = wasInert;
    }
    this.#suppressed.clear();
  }

  /** @param {Event} [_event] */
  async selectKitVariant(_event) {
    const generation = ++this.#generation;
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.#kitReady = false;
    this.#setKitBusy(true);
    this.refs.status.textContent = '';
    this.refs.retryButton.hidden = true;

    try {
      const url = new URL(this.dataset.kitUrl ?? '', window.location.origin);
      const optionValues = Array.from(this.querySelectorAll('select[data-kit-option]')).map((select) => /** @type {HTMLSelectElement} */ (select).value);
      url.searchParams.delete('variant');
      url.searchParams.set('section_id', 'ring-sizing-kit');
      url.searchParams.set('option_values', optionValues.join(','));
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error(`Sizing kit request failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request.signal.aborted || generation !== this.#generation || !this.isConnected) return;

      const content = html.querySelector('[data-isolated-product]');
      const sourceId = content?.getAttribute('data-instance-id');
      const instanceId = this.dataset.instanceId;
      if (!(content instanceof HTMLElement) || !sourceId || !instanceId) {
        throw new Error('Sizing kit response is missing its product content');
      }
      for (const node of content.querySelectorAll('[id], [for], [form], [aria-labelledby], [aria-describedby]')) {
        for (const attribute of ['id', 'for', 'form', 'aria-labelledby', 'aria-describedby']) {
          const value = node.getAttribute(attribute);
          if (value) node.setAttribute(attribute, value.replaceAll(sourceId, instanceId));
        }
      }
      content.dataset.instanceId = instanceId;
      content.dataset.hydrationKey = this.refs.kitContent.dataset.hydrationKey;
      content.id = this.refs.kitContent.id;
      const focusId = document.activeElement instanceof HTMLElement ? document.activeElement.id : '';

      // Both render paths include the same style owners on the initial page.
      await morphSection(content.id.replace(/^shopify-section-/, ''), content.outerHTML, { mode: 'hydration' });
      if (request.signal.aborted || generation !== this.#generation || !this.isConnected) return;
      super.updatedCallback();
      this.#kitReady = true;
      const variantInput = this.refs.kitContent.querySelector('input[name="id"]');
      variantInput?.dispatchEvent(new Event('change', { bubbles: true }));
      if (focusId && !this.refs.kitPanel.hidden) {
        const control = document.getElementById(focusId);
        if (control && this.contains(control)) control.focus({ preventScroll: true });
      }
    } catch (error) {
      if (request.signal.aborted || generation !== this.#generation || !this.isConnected) return;
      this.refs.status.textContent = this.dataset.errorMessage ?? '';
      this.refs.retryButton.hidden = false;
      console.warn('[ring-sizing] Unable to resolve sizing kit options:', error);
    } finally {
      if (generation === this.#generation && this.isConnected) this.#setKitBusy(false);
    }
  }

  /** @param {boolean} busy */
  #setKitBusy(busy) {
    this.refs.kitContent.setAttribute('aria-busy', String(busy));
    const form = this.refs.kitContent.querySelector('product-form-component');
    if (form instanceof HTMLElement) form.inert = busy || !this.#kitReady;
  }
}

if (!customElements.get('ring-sizing-component')) {
  customElements.define('ring-sizing-component', RingSizingComponent);
}
